import type { Observable } from 'rxjs';
import { of, shareReplay } from 'rxjs';
import type { HttpDataResponse } from '@common/types/http-response.type';

import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';

import type { HttpErrorResponse } from '@angular/common/http';

import { BaseHttpService } from '@common/services/REST/base.http.service';
import { type CatalogCategory, type CatalogProductCard } from '@web/widgets/product-card/type/product-card-widget.type';
import type {
    EnabledCategoryIds,
    LandingProductCategory,
} from '@web/widgets/catalog-products/utils/enabled-categories.util';
import { toEnabledCategoryIds } from '@web/widgets/catalog-products/utils/enabled-categories.util';

type PayloadData = {
    categoryId?: number;
    search?: string;
    offset?: number;
    limit?: number;
    fromUpdatedAt?: string;
    ids?: number[];
};

@Injectable({
    providedIn: 'root',
})
export class ProductCatalogHttpService extends BaseHttpService {
    public getUserCategories(): Observable<CatalogCategory[]> {
        return this.http
            .get<
                HttpDataResponse<{ tree: CatalogCategory[]; count: number }>
            >(this.formatCRMServiceApiUrl('products/categories'))
            .pipe(
                shareReplay(),
                map((res) => res?.data?.tree)
            );
    }

    public getCategory(category: number): Observable<CatalogCategory> {
        return this.http
            .get<HttpDataResponse<CatalogCategory>>(this.formatCRMServiceApiUrl(`products/categories/${category}`))
            .pipe(
                shareReplay(),
                map((res) => res.data)
            );
    }

    public getProductById(productId: number): Observable<CatalogProductCard> {
        return this.http
            .get<HttpDataResponse<CatalogProductCard>>(this.formatCRMServiceApiUrl(`products/${productId}`))
            .pipe(
                shareReplay(),
                map((res) => {
                    return res.data;
                })
            );
    }

    public getAllCatalogProducts(
        payloadData: PayloadData = {
            categoryId: null,
            search: null,
            offset: 0,
            limit: 20,
        }
    ): Observable<{ products: CatalogProductCard[]; total: number }> {
        const { categoryId, search, offset, limit, fromUpdatedAt, ids } = payloadData;
        return this.http
            .post<{ data: CatalogProductCard[]; total: number }>(this.formatCRMServiceApiUrl(`products/all`), {
                limit,
                offset,
                categoryId,
                fromUpdatedAt,
                ids,
                search,
                orderBy: {
                    fieldName: 'createdAt',
                    direction: 'desc',
                },
            })
            .pipe(
                shareReplay(),
                map(({ data, total }) => ({
                    products: data,
                    total: total,
                }))
            );
    }

    /**
     * Ids of the categories the online store publishes on the given site, null when they are unknown.
     * A null answer hides every product rather than showing all of them — an unpublished category
     * must not leak because of a failed request.
     */
    public retrieveLandingProductCategories(siteId: number): Observable<EnabledCategoryIds> {
        return this.http
            .get<
                HttpDataResponse<{ siteId: number; categoryIds: LandingProductCategory[] }>
            >(this.formatCRMServiceApiUrl(`products/categories/landing-sites/${siteId}`))
            .pipe(
                shareReplay(),
                map((res) => toEnabledCategoryIds(res?.data?.categoryIds)),
                catchError((error: HttpErrorResponse) => {
                    console.error('Failed to load the enabled product categories', error);

                    return of(null);
                })
            );
    }
}
